<template>
  <div
    :class="[$style.base, { [$style.touch]: device.touch}]"
    v-test="'_base-gallery-image'"
  >
    <div :class="$style.container">
      <div :class="$style.actions">
        <div
          :class="[$style.action, $style.actionClickable]"
          @click="removeImage"
          v-test="'_base-gallery-remove'"
        >
          <BaseIcon
            name="delete"
            size="s"
            color="error"
          />
        </div>
        <div :class="$style.action">
          <BaseIcon
            name="drag"
            size="s"
          />
        </div>
      </div>
      <div :class="$style.image">
        <transition appear>
          <img
            v-if="imageLoaded"
            :src="imageUrl"
          >
        </transition>
      </div>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { device } from '@/user-context';

const props = defineProps<{
  imageUrl: string;
}>();

const emit = defineEmits(['remove']);

const removeImage = () => {
  emit('remove');
};

const imageLoaded = ref(false);

watch(
  () => props.imageUrl,
  (url) => {
    const finish = () => {
      imageLoaded.value = true;
    };

    const image = new Image();

    image.onload = finish;
    image.onerror = finish;
    image.src = url;

    if (image.complete) {
      finish();
    }
  },
  {
    immediate: true,
  },
);
</script>

<style
  module
  lang="scss"
>

.base {
  border-radius: 16px;
  cursor: grab;
}

.container {
  position: relative;
  overflow: hidden;
  clip-path: inset(0 round 12px);
}

.base:global(.sortable-ghost) {
  border: 2px dashed var(--border-strong);

  .container {
    transform: scale(0.95);
  }

  .image {
    opacity: .5;
  }
}

.actions {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 50%;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding: $spacing * .5;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  transition: opacity 0.1s ease-out;
}

.base:not(.touch) {
  .actions, .action {
    opacity: 0;
    pointer-events: none;
  }

  .action {
    transform: scale(.8);
  }

  @include hover {
    .actions, .action {
      opacity: 1;
      pointer-events: auto;
    }

    .action {
      transform: scale(1);
    }
  }
}

.action {
  background-color: var(--surface-primary);
  border-radius: $radius;
  padding: $spacing * .5;
  transition: transform 0.15s ease-out;
  display: flex;
  align-items: center;
}

.actionClickable {
  cursor: pointer;
}

.image {
  position: relative;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  overflow: hidden;
  background-color: var(--highlight);

  img {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;

    &:global(.v-enter-from) {
      transform: scale(.9);
      opacity: 0;
    }

    &:global(.v-enter-active) {
      transition: transform .15s $easeOutBack, opacity .15s ease-out;
    }
  }
}
</style>
